import { useState } from 'react'
import { cn } from '~/utils/tailwind-merge.ts'

// ── Types ──────────────────────────────────────────────────────

type InterfaceOption = 'payment-links' | 'checkout' | 'elements' | 'custom-ui'
type BusinessModel = 'one-time' | 'subscriptions' | 'marketplace'

type InterfaceSpec = {
	nodeSubtitle: string
	routesToServer: boolean
	serverActive: boolean
	serverLabel: string
	serverProminent: boolean
	bypassLabel?: string
	returnLabel?: string
}

type BusinessModelSpec = {
	bankBoxes: Array<string>
	showRecurring: boolean
}

type SelectorBarProps<T extends string> = {
	label: string
	options: Array<{ value: T; label: string }>
	value: T
	onChange: (v: T) => void
}

type FlowNodeProps = {
	label: string
	subtitle?: string
	active: boolean
	prominent?: boolean
	style?: React.CSSProperties
}

type ArrowProps = {
	label?: string
	active: boolean
	style?: React.CSSProperties
}

type StyleProps = {
	style?: React.CSSProperties
}

type InterfacePillsProps = {
	options: Array<{ value: InterfaceOption; label: string }>
	value: InterfaceOption
	onChange: (v: InterfaceOption) => void
}

// ── Data ───────────────────────────────────────────────────────

const INTERFACE_OPTIONS: Array<{ value: InterfaceOption; label: string }> = [
	{ value: 'payment-links', label: 'Payment Links' },
	{ value: 'checkout', label: 'Checkout' },
	{ value: 'elements', label: 'Elements' },
	{ value: 'custom-ui', label: 'Custom UI' },
]

const BUSINESS_MODEL_OPTIONS: Array<{ value: BusinessModel; label: string }> = [
	{ value: 'one-time', label: 'One-time' },
	{ value: 'subscriptions', label: 'Subscriptions' },
	{ value: 'marketplace', label: 'Marketplace' },
]

const INTERFACE_SPECS: Record<InterfaceOption, InterfaceSpec> = {
	'payment-links': {
		nodeSubtitle: 'Stripe-hosted',
		routesToServer: false,
		serverActive: false,
		serverLabel: '',
		serverProminent: false,
	},
	checkout: {
		nodeSubtitle: 'Stripe-hosted',
		routesToServer: false,
		serverActive: true,
		serverLabel: '① create session',
		serverProminent: false,
		bypassLabel: '② redirect',
		returnLabel: '③ /success',
	},
	elements: {
		nodeSubtitle: 'self-hosted',
		routesToServer: true,
		serverActive: true,
		serverLabel: 'create PI',
		serverProminent: false,
	},
	'custom-ui': {
		nodeSubtitle: 'self-hosted',
		routesToServer: true,
		serverActive: true,
		serverLabel: 'full API',
		serverProminent: true,
	},
}

const BUSINESS_MODEL_SPECS: Record<BusinessModel, BusinessModelSpec> = {
	'one-time': {
		bankBoxes: ['Your Bank'],
		showRecurring: false,
	},
	subscriptions: {
		bankBoxes: ['Your Bank'],
		showRecurring: true,
	},
	marketplace: {
		bankBoxes: ['Platform', 'Seller A', 'Seller B'],
		showRecurring: false,
	},
}

const AFTER_MONEY_ITEMS = ['Refund', 'Dispute', 'Tax & Reporting'] as const

const FLOW_DESCRIPTIONS: Record<`${InterfaceOption}-${BusinessModel}`, string> = {
	'payment-links-one-time':
		'The simplest setup. You create a Payment Link in the Stripe Dashboard — no code needed. Customers click the link, pay on Stripe\'s hosted page, and the money flows to your balance. Your server only hears about it via webhooks.',
	'payment-links-subscriptions':
		'Payment Links can handle subscriptions too. You create a recurring Payment Link in the Dashboard. Stripe handles the hosted page, stores the payment method, and automatically charges the customer on each billing cycle.',
	'payment-links-marketplace':
		'Payment Links with Connect. You create links that split payments between your platform and connected sellers. Stripe handles the hosted page and distributes funds to multiple bank accounts.',
	'checkout-one-time':
		'The most common integration. Your server creates a Checkout Session, Stripe hosts the payment page. The customer pays, Stripe sends a webhook, you fulfill the order. Clean separation between your code and payment UI.',
	'checkout-subscriptions':
		'Your server creates a Checkout Session in subscription mode. Stripe hosts the payment page, stores the card, and handles all recurring billing automatically. You get webhooks for each renewal.',
	'checkout-marketplace':
		'Checkout with Connect. Your server creates a session that splits the payment. Stripe hosts the page, processes the charge, and distributes funds to your platform and connected sellers automatically.',
	'elements-one-time':
		'You embed Stripe\'s payment form components directly in your page for a branded experience. Your server creates a Payment Intent, the iframe-based Elements collect card details securely, and you confirm the payment.',
	'elements-subscriptions':
		'Elements embedded in your page for branded recurring billing. Your server creates a subscription via the API, Elements collect the payment method, and Stripe handles all subsequent charges.',
	'elements-marketplace':
		'Elements with Connect for a branded marketplace checkout. Your page embeds the payment form, your server coordinates the split between platform and sellers, and Stripe distributes funds accordingly.',
	'custom-ui-one-time':
		'Full control. You build your own payment form, collect card details via the API (PCI compliance required), create Payment Intents, and handle every step of the flow. Maximum flexibility, maximum responsibility.',
	'custom-ui-subscriptions':
		'Full API control over recurring billing. You build the payment form, manage subscription creation, handle payment method storage, and control the entire billing lifecycle through Stripe\'s API.',
	'custom-ui-marketplace':
		'The most complex integration. You build everything — payment form, platform/seller split logic, payout scheduling — all through direct API calls to Stripe Connect. Full control over multi-party money flow.',
}

// ── Helpers ────────────────────────────────────────────────────

function opacityClass(active: boolean): string {
	return active ? 'opacity-100' : 'opacity-[0.15]'
}

// ── Sub-components ─────────────────────────────────────────────

function SelectorPills<T extends string>({ label, options, value, onChange }: SelectorBarProps<T>) {
	return (
		<div className="flex flex-wrap items-center gap-2">
			<span className="text-[10px] font-semibold uppercase tracking-wider opacity-60">{label}</span>
			<div className="flex flex-wrap gap-1">
				{options.map(opt => (
					<button
						key={opt.value}
						type="button"
						onClick={() => onChange(opt.value)}
						className={cn(
						'rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-all duration-200',
						opt.value === value
							? 'bg-brand text-white'
							: 'bg-muted-800 text-muted-300 hover:bg-muted-700 hover:text-muted-100 dark:bg-muted-200 dark:text-muted-600 dark:hover:bg-muted-300 dark:hover:text-muted-800',
					)}
					>
						{opt.label}
					</button>
				))}
			</div>
		</div>
	)
}

function FlowNode({ label, subtitle, active, prominent, style }: FlowNodeProps) {
	const borderStyle = prominent
		? 'border-brand/50 bg-brand/10 dark:border-brand/40 dark:bg-brand/5'
		: 'border-muted-700 bg-muted-800 dark:border-muted-300 dark:bg-muted-200'

	return (
		<div
			className={cn('flex shrink-0 flex-col items-center justify-center rounded border px-2 py-1.5 text-center transition-all duration-300', opacityClass(active), borderStyle)}
			style={style}
		>
			<span className="text-[11px] font-semibold leading-tight">{label}</span>
			{subtitle ? <span className="text-[9px] leading-tight opacity-60">{subtitle}</span> : null}
		</div>
	)
}

function Arrow({ label, active, style }: ArrowProps) {
	return (
		<div className={cn('flex shrink-0 flex-col items-center transition-opacity duration-300', opacityClass(active))} style={style}>
			<svg width="24" height="16" viewBox="0 0 24 16" fill="none" className="text-current">
				<line x1="0" y1="8" x2="18" y2="8" stroke="currentColor" strokeWidth="1.5" />
				<polyline points="15,3 21,8 15,13" stroke="currentColor" strokeWidth="1.5" fill="none" />
			</svg>
			{label ? <span className="whitespace-nowrap text-[8px] leading-tight opacity-60">{label}</span> : null}
		</div>
	)
}

function BypassArrow({ label, returnLabel, style }: { label?: string; returnLabel?: string } & StyleProps) {
	return (
		<div className="flex flex-col gap-0.5" style={style}>
			{label && <span className="text-center text-[8px] leading-tight opacity-60">{label}</span>}
			<div className="flex items-center">
				<div className="h-[1.5px] flex-1 bg-current" />
				<svg width="10" height="16" viewBox="0 0 10 16" fill="none" className="shrink-0 text-current">
					<polyline points="1,3 9,8 1,13" stroke="currentColor" strokeWidth="1.5" fill="none" />
				</svg>
			</div>
			{returnLabel && (
				<>
					<div className="flex items-center opacity-60">
						<svg width="10" height="12" viewBox="0 0 10 12" fill="none" className="shrink-0 text-current">
							<polyline points="9,1 1,6 9,11" stroke="currentColor" strokeWidth="1" fill="none" />
						</svg>
						<div className="flex-1 border-t border-dashed border-current" />
					</div>
					<span className="text-center text-[8px] leading-tight opacity-60">{returnLabel}</span>
				</>
			)}
		</div>
	)
}

function ServerApiArrow({ label, style }: { label: string } & StyleProps) {
	return (
		<div className="flex flex-col items-center gap-0.5" style={style}>
			<div className="flex w-full items-center">
				<div className="flex-1 border-t border-current" style={{ borderWidth: '1.5px' }} />
				<svg width="8" height="10" viewBox="0 0 8 10" fill="none" className="shrink-0 text-current">
					<polyline points="1,1 7,5 1,9" stroke="currentColor" strokeWidth="1.5" fill="none" />
				</svg>
			</div>
			<span className="whitespace-nowrap text-[8px] leading-tight opacity-60">{label}</span>
		</div>
	)
}

function WebhookArrow({ style }: StyleProps) {
	return (
		<div className="flex flex-col items-center gap-0.5" style={style}>
			<span className="text-[9px] font-medium uppercase tracking-wider opacity-50">Webhooks</span>
			<div className="flex w-full items-center opacity-70">
				<svg width="8" height="10" viewBox="0 0 8 10" fill="none" className="shrink-0 text-current">
					<polyline points="7,1 1,5 7,9" stroke="currentColor" strokeWidth="1" fill="none" />
				</svg>
				<div className="flex-1 border-t border-dashed border-current" />
			</div>
		</div>
	)
}

function AfterMoneyMoves({ style }: StyleProps) {
	return (
		<div className="flex flex-wrap items-center justify-center gap-2 border-t border-dashed border-muted-700 pt-2.5 dark:border-muted-300" style={style}>
			<span className="text-[9px] font-semibold uppercase tracking-wider opacity-40">After money moves</span>
			{AFTER_MONEY_ITEMS.map(item => (
				<span key={item} className="rounded border border-muted-700 px-1.5 py-0.5 text-[9px] opacity-50 dark:border-muted-300">
					{item}
				</span>
			))}
		</div>
	)
}

function InterfacePills({ options, value, onChange }: InterfacePillsProps) {
	return (
		<div className="flex flex-col items-center gap-1">
			<span className="text-[10px] font-semibold uppercase tracking-wider opacity-60">Interface</span>
			{options.map(opt => (
				<button
					key={opt.value}
					type="button"
					onClick={() => onChange(opt.value)}
					className={cn(
						'w-full whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] font-medium transition-all duration-200',
						opt.value === value
							? 'bg-brand text-white'
							: 'bg-muted-700 text-muted-300 hover:bg-muted-600 hover:text-muted-100 dark:bg-muted-300 dark:text-muted-600 dark:hover:bg-muted-400 dark:hover:text-muted-800',
					)}
				>
					{opt.label}
				</button>
			))}
		</div>
	)
}

function DescriptionPanel({ text }: { text: string }) {
	return (
		<div className="border-t border-muted-700 pt-3 dark:border-muted-300">
			<p className="text-[11px] leading-relaxed opacity-80">{text}</p>
		</div>
	)
}

// ── Main component ─────────────────────────────────────────────

function StripeFlowDiagram() {
	const [interfaceOption, setInterfaceOption] = useState<InterfaceOption>('checkout')
	const [businessModel, setBusinessModel] = useState<BusinessModel>('one-time')

	const ifaceSpec = INTERFACE_SPECS[interfaceOption]
	const modelSpec = BUSINESS_MODEL_SPECS[businessModel]
	const description = FLOW_DESCRIPTIONS[`${interfaceOption}-${businessModel}`]

	const showBypass = !ifaceSpec.routesToServer

	// Row assignments — bypass mode adds a dedicated webhook row
	// Non-bypass: pills(1) flow(2) webhook(3) afterMoney(4) description(5)
	// Bypass:     pills(1) bypass(2) server(3) webhook(4) afterMoney(5) description(6)
	const flowRow = showBypass ? '2 / 5' : '2'
	const serverNodeRow = showBypass ? '3 / 5' : '2'
	const serverContentRow = showBypass ? '3' : '2'
	const webhookRow = showBypass ? 4 : 3
	const afterMoneyRow = webhookRow + 1
	const descriptionRow = webhookRow + 2

	return (
		<div className="my-space-13 overflow-x-auto rounded-lg bg-muted-900 px-4 py-5 text-muted-100 dark:bg-muted-100 dark:text-muted-900">
			<div style={{ minWidth: '560px' }}>
				{/* CSS Grid: 11 columns — node, arrow, node, arrow, ... (col 6 wider for webhook label) */}
				<div
					className="grid items-center gap-y-3"
					style={{ gridTemplateColumns: 'auto 24px auto 24px auto 80px auto 24px auto 24px auto' }}
				>
					{/* Row 1: Business Model pills — spans all columns */}
					<div style={{ gridRow: 1, gridColumn: '1 / -1' }}>
						<SelectorPills label="Business Model" options={BUSINESS_MODEL_OPTIONS} value={businessModel} onChange={setBusinessModel} />
					</div>

					{/* Bypass arrow — customer goes directly to Stripe (PL/Checkout) */}
					{showBypass && (
						<BypassArrow
							label={ifaceSpec.bypassLabel}
							returnLabel={ifaceSpec.returnLabel}
							style={{ gridRow: 2, gridColumn: '4 / 7' }}
						/>
					)}

					{/* Main flow nodes — span 3 rows when bypass is shown (bypass + server + webhook) */}
					<FlowNode label="Customer" active style={{ gridRow: flowRow, gridColumn: 1 }} />
					<Arrow active style={{ gridRow: flowRow, gridColumn: 2 }} />

					{/* Interface node with selector pills inside */}
					<div
						className="flex shrink-0 flex-col items-center gap-1.5 rounded border border-muted-700 bg-muted-800 px-2.5 py-2 text-center dark:border-muted-300 dark:bg-muted-200"
						style={{ gridRow: flowRow, gridColumn: 3 }}
					>
						<InterfacePills options={INTERFACE_OPTIONS} value={interfaceOption} onChange={setInterfaceOption} />
						<span className="text-[9px] leading-tight opacity-60">{ifaceSpec.nodeSubtitle}</span>
					</div>

					{/* Server corridor — col 4: arrow to Server, col 5: Server, col 6: arrow from Server */}
					{ifaceSpec.routesToServer && (
						<Arrow active style={{ gridRow: serverContentRow, gridColumn: 4 }} />
					)}
					<FlowNode
						label="Server"
						active={ifaceSpec.serverActive}
						prominent={ifaceSpec.serverProminent}
						style={{ gridRow: serverNodeRow, gridColumn: 5, ...(showBypass && { alignSelf: 'stretch' }) }}
					/>
					{ifaceSpec.serverActive && (
						<ServerApiArrow
							label={ifaceSpec.serverLabel}
							style={{ gridRow: serverContentRow, gridColumn: 6 }}
						/>
					)}

					{/* Stripe and onwards */}
					<FlowNode label="Stripe" active style={{ gridRow: flowRow, gridColumn: 7, ...(showBypass && { alignSelf: 'stretch' }) }} />
					<Arrow active style={{ gridRow: flowRow, gridColumn: 8 }} />

					<div className="flex flex-col items-center" style={{ gridRow: flowRow, gridColumn: 9 }}>
						<FlowNode label="Balance" active />
						{modelSpec.showRecurring && <span className="mt-0.5 text-[8px] font-medium text-brand">↻ recurring</span>}
					</div>

					<Arrow active style={{ gridRow: flowRow, gridColumn: 10 }} />

					<div className="flex flex-col gap-0.5" style={{ gridRow: flowRow, gridColumn: 11 }}>
						{modelSpec.bankBoxes.map(bank => (
							<FlowNode key={bank} label={bank} active />
						))}
					</div>

					{/* Webhook arrow — own row, subgrid spans Server through Stripe columns */}
					<WebhookArrow style={{ gridRow: webhookRow, gridColumn: 6 }} />

					{/* After money moves — spans all columns */}
					<AfterMoneyMoves style={{ gridRow: afterMoneyRow, gridColumn: '1 / -1' }} />

					{/* Description panel — spans all columns */}
					<div style={{ gridRow: descriptionRow, gridColumn: '1 / -1' }}>
						<DescriptionPanel text={description} />
					</div>
				</div>
			</div>
		</div>
	)
}

export { StripeFlowDiagram }
